<template>
  <div class="human-verification-root">
    <div
      v-show="state.verified"
      class="uplus-site-content"
      :aria-hidden="state.verified ? 'false' : 'true'"
    >
      <App v-if="entered" />
    </div>

    <section
      v-if="!state.verified"
      class="uplus-verification-gate"
      aria-labelledby="uplus-gate-title"
    >
      <div class="uplus-gate-card">
        <h1 id="uplus-gate-title">{{ text.title }}</h1>
        <p>{{ text.intro }}</p>
        <div
          class="uplus-gate-challenge"
          :data-phase="busy ? state.phase : 'error'"
        >
          <div ref="widget" class="uplus-gate-widget"></div>
          <div
            v-show="!busy || state.phase !== 'challenge'"
            class="uplus-gate-feedback"
          >
            <p class="uplus-gate-status" role="status">{{ statusText }}</p>
          </div>
        </div>
        <button
          v-if="busy"
          class="uplus-gate-action uplus-gate-cancel"
          type="button"
          @click="cancelVerification"
        >
          {{ text.cancel }}
        </button>
        <button
          v-else
          ref="retry"
          class="uplus-gate-action uplus-gate-retry"
          type="button"
          @click="retryVerification"
        >
          {{ text.retry }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { baseUrl } from '@/utils/request'
import { readLanguage } from '@/utils/locale'
import {
  cancelHumanVerification,
  checkVerificationAccess,
  ensureHumanVerification,
  humanVerificationState,
  resumeHumanVerification,
  setVerificationContainer
} from '@/utils/humanVerification'

const copy = {
  'zh-CN': {
    title: '安全验证',
    intro: '完成安全验证后即可进入 UPlusTrade。',
    pending: '正在准备安全验证…',
    loading: '正在加载验证码…',
    challenge: '请完成安全验证。',
    verifying: '正在确认验证结果，请稍候…',
    cancelled: '验证尚未完成，请继续验证后进入网站。',
    invalid: '验证已失效，请重新验证后继续访问。',
    unavailable: '验证服务暂不可用，请稍后重试。',
    retry: '继续验证',
    cancel: '取消',
    protected: '安全访问 · UPlusTrade'
  },
  'zh-HK': {
    title: '安全驗證',
    intro: '完成安全驗證後即可進入 UPlusTrade。',
    pending: '正在準備安全驗證…',
    loading: '正在載入驗證碼…',
    challenge: '請完成安全驗證。',
    verifying: '正在確認驗證結果，請稍候…',
    cancelled: '驗證尚未完成，請繼續驗證後進入網站。',
    invalid: '驗證已失效，請重新驗證後繼續瀏覽。',
    unavailable: '驗證服務暫不可用，請稍後重試。',
    retry: '繼續驗證',
    cancel: '取消',
    protected: '安全存取 · UPlusTrade'
  },
  en: {
    title: 'Security check',
    intro: 'Complete verification to enter UPlusTrade.',
    pending: 'Preparing your security check…',
    loading: 'Loading your security check…',
    challenge: 'Complete the security check.',
    verifying: 'Confirming your verification…',
    cancelled: 'Verification is incomplete. Complete the check to enter.',
    invalid: 'Verification expired. Verify again to continue.',
    unavailable: 'Verification is unavailable. Please try again later.',
    retry: 'Continue verification',
    cancel: 'Cancel',
    protected: 'Secure access · UPlusTrade'
  },
  ko: {
    title: '보안 인증',
    intro: '보안 인증을 완료하면 UPlusTrade에 접속할 수 있습니다.',
    pending: '보안 인증을 준비하는 중…',
    loading: '보안 인증을 불러오는 중…',
    challenge: '보안 인증을 완료해 주세요.',
    verifying: '인증 결과를 확인하는 중…',
    cancelled: '인증이 완료되지 않았습니다. 인증을 완료해 주세요.',
    invalid: '인증이 만료되었습니다. 계속하려면 다시 인증해 주세요.',
    unavailable: '현재 인증을 사용할 수 없습니다. 잠시 후 다시 시도해 주세요.',
    retry: '인증 계속하기',
    cancel: '취소',
    protected: '안전한 접속 · UPlusTrade'
  }
}

export default {
  name: 'HumanVerificationGate',
  components: {
    App: () => import('@/App.vue')
  },
  data() {
    return {
      state: humanVerificationState,
      entered: humanVerificationState.verified,
      locale: readLanguage(),
      controller: null
    }
  },
  computed: {
    text() {
      return copy[this.locale] || copy['zh-HK']
    },
    busy() {
      return !this.state.notice || this.state.notice.retrying
    },
    statusText() {
      if (this.busy) return this.text[this.state.phase] || this.text.pending
      if (this.state.notice.code === 'cancelled') return this.text.cancelled
      if (this.state.notice.code === 'invalid') return this.text.invalid
      return this.text.unavailable
    }
  },
  watch: {
    'state.verified'(verified) {
      if (verified) {
        this.entered = true
        return
      }
      this.$nextTick(() => {
        if (this.state.notice && !this.state.notice.retrying && this.$refs.retry) {
          this.$refs.retry.focus()
        } else if (!this.state.notice) {
          this.startVerification()
        }
      })
    },
    'state.notice'(notice) {
      if (notice && !notice.retrying) {
        this.$nextTick(() => {
          if (this.$refs.retry) this.$refs.retry.focus()
        })
      }
    }
  },
  mounted() {
    setVerificationContainer(this.$refs.widget)
    this.startVerification()
    window.addEventListener('focus', this.checkAccess)
    window.addEventListener('pageshow', this.checkAccess)
    window.addEventListener('hashchange', this.checkAccess)
    window.addEventListener('popstate', this.checkAccess)
    window.addEventListener('uplus-language-change', this.handleLanguageChange)
    document.addEventListener('visibilitychange', this.checkAccess)
  },
  beforeDestroy() {
    if (this.controller) this.controller.abort()
    setVerificationContainer(null)
    window.removeEventListener('focus', this.checkAccess)
    window.removeEventListener('pageshow', this.checkAccess)
    window.removeEventListener('hashchange', this.checkAccess)
    window.removeEventListener('popstate', this.checkAccess)
    window.removeEventListener('uplus-language-change', this.handleLanguageChange)
    document.removeEventListener('visibilitychange', this.checkAccess)
  },
  methods: {
    startVerification() {
      if (this.state.verified || this.state.notice || this.controller) return
      this.controller = new AbortController()
      ensureHumanVerification(this.controller.signal, false, baseUrl)
        .catch(() => {})
        .finally(() => {
          this.controller = null
        })
    },
    retryVerification() {
      resumeHumanVerification(baseUrl)
    },
    cancelVerification() {
      cancelHumanVerification()
    },
    checkAccess() {
      checkVerificationAccess(baseUrl)
    },
    handleLanguageChange(event) {
      this.locale = (event && event.detail) || readLanguage()
    }
  }
}
</script>

<style scoped>
.human-verification-root {
  min-height: 100vh;
}
.uplus-site-content[style*="display: none"] { display: none !important; }
.uplus-verification-gate {
  position: fixed; inset: 0; z-index: 200;
  display: block; overflow: auto; overscroll-behavior: contain;
  background: #fff; color: #172033; font: 16px/1.65 system-ui, sans-serif;
}
.uplus-gate-card {
  box-sizing: border-box; width: 100%; max-width: 760px;
  margin: 40px auto; padding: 0 10px;
}
.uplus-gate-card h1 {
  margin: 0 0 16px; color: inherit; font: 600 25px/1.5 system-ui, sans-serif;
}
.uplus-gate-card p {
  margin: 16px 0; color: inherit; font: inherit; overflow-wrap: anywhere;
}
.uplus-gate-challenge {
  display: grid; min-width: 0; margin: 18px 0;
}
.uplus-gate-widget,
.uplus-gate-feedback {
  grid-area: 1 / 1; min-width: 0; min-height: 65px;
}
.uplus-gate-widget {
  display: block; width: 100%;
}
.uplus-gate-feedback {
  position: relative; display: flex; align-items: center; background: #fff;
}
.uplus-gate-card .uplus-gate-status {
  margin: 0;
}
.uplus-gate-card .uplus-gate-action {
  display: inline-block; width: auto; padding: 10px 14px; margin: 0;
  border: 1px solid #64748b; border-radius: 4px; background: #fff; color: #172033;
  font: inherit; cursor: pointer;
}
.uplus-gate-card .uplus-gate-action:hover { background: #f8fafc; }
.uplus-gate-card .uplus-gate-action:focus-visible {
  outline: 2px solid #64748b; outline-offset: 3px;
}
</style>
